import { FC, Fragment, useMemo } from 'react';

import DOMPurify from 'dompurify';
import katex from 'katex';

import 'katex/dist/katex.min.css';

import { parseLatexSegments } from './parseLatexSegments';
import type { LatexMathSegment } from './parseLatexSegments';

type LatexTextProps = {
  text: string;
  /**
   * Hides math segments whose closing delimiter has not arrived yet — useful
   * while the text is being revealed by a typewriter animation, so raw LaTeX
   * source never flashes on screen.
   */
  hideIncompleteMath?: boolean;
};

const renderMathToHtml = (segment: LatexMathSegment) => {
  try {
    const html = katex.renderToString(segment.content, {
      displayMode: segment.displayMode,
      throwOnError: false,
      strict: 'ignore',
      output: 'htmlAndMathml',
    });
    return DOMPurify.sanitize(html);
  } catch {
    return null;
  }
};

/**
 * Renders text with embedded LaTeX math (`$…$`, `$$…$$`, `\(…\)`, `\[…\]`) as
 * KaTeX HTML. Text without math delimiters renders as plain text, unchanged.
 */
const LatexText: FC<LatexTextProps> = ({ text, hideIncompleteMath = false }) => {
  const segments = useMemo(() => parseLatexSegments(text), [text]);

  return (
    <>
      {segments.map((segment, index) => {
        // Segment order is stable for a given text; index is a safe key here.
        const key = `${segment.type}-${index}`;

        if (segment.type === 'text') {
          return <Fragment key={key}>{segment.content}</Fragment>;
        }

        if (segment.incomplete) {
          return hideIncompleteMath ? null : <Fragment key={key}>{segment.raw}</Fragment>;
        }

        const html = renderMathToHtml(segment);
        if (html == null) {
          return <Fragment key={key}>{segment.raw}</Fragment>;
        }

        // eslint-disable-next-line react/no-danger
        return <span key={key} dangerouslySetInnerHTML={{ __html: html }} />;
      })}
    </>
  );
};

export default LatexText;
